import {Injectable} from '@angular/core';
import {Container, Image, ImageBox, ImageSection, Tab} from '../model/layout.model';
import {DocumentModel} from '../model/document.model';
import {
  KiBoxWerte,
  KiGebiet,
  KiGebiete,
  KiGrafikAnfrage,
  KiRegionen,
  KiZeit
} from '../model/ki-grafik.model';
import {KiErgebnis, KiTextEintrag} from './ki-ergebnis.service';
import {GrafikUmfang, GrafikUmfangArt} from '../model/event-bus.model';

/** Eine aus dem Datensatz wiederhergestellte KI-Antwort samt ihrem Umfang. */
export interface GespeichertesKiErgebnis {
  umfang: GrafikUmfang;
  ergebnis: KiErgebnis;
}

/**
 * Welche Werte in eine Anfrage gehören.
 *
 * - `alle` — der Reiter- bzw. Zeilen-Text: Temperaturen immer (Messwerte), Symbole nur
 *   mit gelbem Umschalter; Wind wie das Symbol, sobald seine Box einen Umschalter trägt
 *   (NRW), sonst immer (COSMO, Lokalzeit).
 * - `schlagzeile` — die Schlagzeile: **ohne** Temperaturen und **ohne** Wind, weil sie
 *   die Lage in einem Satz nennt und keine Messwerte aufzählt. Ausnahme: **Sturm** – der
 *   gehört in jede Schlagzeile.
 */
export type KiWertauswahl = 'alle' | 'schlagzeile' | 'kifelder';

/** Eine angehakte Box, bevor die Regionsnamen auf Eindeutigkeit geprüft sind. */
interface KiBoxEintrag {
  /** Name der Region: `region`, ersatzweise `city`, ersatzweise die Box-Id. */
  name: string;
  /** `containerId` der Karte – qualifiziert den Namen, wenn er mehrfach vorkommt. */
  karte: string;
  /** Die gefüllten Werte der Box. */
  werte: KiBoxWerte;
  /**
   * Nummer des gelben Kästchens in der Reihenfolge des Anhakens (`ki:order:<boxId>` an der
   * Karte, siehe `KiReihenfolgeService`) – nur bei angehakter Box. Bewusst neben `werte`,
   * damit sie beim Verschmelzen gleichnamiger Regionen nicht als Feld-Überschneidung zählt.
   */
  order?: number;
}

/** Die gebaute Anfrage – oder Klartext, warum keine zustande kam. */
export interface GrafikKiErgebnis {
  anfrage?: KiGrafikAnfrage;
  /** Anzahl der übernommenen Boxen; taugt für die Rückmeldung an den Redakteur. */
  boxen: number;
  /** Nur gesetzt, wenn `anfrage` fehlt. Taugt als rote Meldung. */
  grund?: string;
}

/**
 * "KI-Texte erzeugen" über den Grafiken: sammelt die Werte der Grafik-Boxen und baut
 * daraus den Anfrage-Körper für den KI-Service.
 *
 * <b>Was mitgeht, hängt am Wert und nicht an der Box:</b> Temperaturen gehen mit, sobald
 * sie **gefüllt** sind – sie sind Messwerte. Wetter-Symbole, Warnschilder und freie
 * Wert-Boxen gehen nur mit, wenn der **gelbe Umschalter `ai`** gesetzt ist (gelbes X mit
 * lila Schimmer) – sie sind die Aussage des Redakteurs, und die trifft er bewusst. Der
 * Wind steht dazwischen: die NRW-Wind-Boxen tragen seit Oktober 2026 denselben gelben
 * Umschalter, und dort geht der Wind **nur angehakt** mit; eine Wind-Box ohne Umschalter
 * (COSMO, Lokalzeit) geht wie bisher mit, sobald sie gefüllt ist. Der rote Umschalter mit
 * dem Ausrufezeichen ist `warn` und hat mit alledem nichts zu tun.
 *
 * Der Unterschied zum älteren Videotext-Weg ("Daten übernehmen"), den man kennen muss:
 * dort stehen Quelle, Ziel und `max_chars` als `KiButtonMapping` **im Server-Template**
 * und die Anfrage geht über `POST chat/hrtext`. Hier ist der Auslöser ein Knopf der
 * Kopfleiste – die steht im Client (`GRAFIK_TOOLBAR`) –, und die Eingabe ist ein
 * JSON-Baum aus den Karten statt einer einzelnen Textbox. Beide Wege gehen aber über
 * denselben wetter-terminal-rest-service ({@link KiChatService}, `POST chat/{rfa}`) –
 * der KI-Springboot-Service dahinter ist zur Laufzeit nicht direkt erreichbar.
 *
 * Wie die Schlüssel des JSON entstehen:
 *
 * | Ebene | Quelle |
 * | --- | --- |
 * | Rundfunkanstalt (`WDR`) | Kürzel der RFA in Großbuchstaben |
 * | Gebiet (`Nordrhein Westfalen`) | Beschriftung des obersten Reiters, Bindestriche zu Leerzeichen |
 * | Zeitraum (`heute_ganz`) | Zeilen-Segment der `containerId` der Karte, Bindestriche zu Unterstrichen; bei Karten mit Sidebar die Abschnitts-Id |
 * | Region (`Sauerland`) | `ImageBox.region`, ersatzweise `city`, ersatzweise die Box-Id |
 *
 * Ein Zeitraum führt die Boxen **aller sieben Karten seiner Zeile** zusammen – so wie
 * es der Service erwartet. Dieselbe Region auf verschiedenen Karten (Symbol- und
 * Wind-Karte führen beide "Niederrhein") wird dabei zu **einem** Eintrag verschmolzen;
 * nur wenn sich die Felder überschneiden, wird mit der `containerId` der Karte
 * qualifiziert – siehe {@link alsRegionen}.
 */
@Injectable({providedIn: 'root'})
export class KiGrafikService {

  /**
   * Der **gelbe** Umschalter einer Box – das Startsignal: nur angehakte Boxen kommen in
   * die Anfrage. Der rote mit dem Ausrufezeichen ist `warn` und hat damit nichts zu tun.
   *
   * Er hieß zunächst `ausfall`; das war ein Übernahmefehler aus der AE-Vorlage, denn
   * eine Ausfall-Markierung ist für ihn nicht vorgesehen – er schaltet die KI-Funktion
   * ein und sonst nichts. Wichtig für gespeicherte Datensätze: der Umschalter steht als
   * `<boxId>::<toggleId>` im Datensatz, ein vor der Umbenennung gespeichertes
   * `<boxId>::ausfall` findet also keinen Umschalter mehr.
   */
  public static readonly KI_UMSCHALTER = 'ai';

  /**
   * Wohin der erzeugte Text geht: **je Reiter eine eigene Textbox** im Reiter "KI-Texte".
   *
   * Der Reiter ist der Zeitpunkt, und die Textbox dazu heißt genauso – "Heute" schreibt
   * nach `wdr.kitexte.heute`, "2.Aussichtentag" nach `wdr.kitexte.aussichten.tag2`.
   * Zugeordnet wird über das Präfix der Karten-`containerId` und **nicht** über die
   * Reiterbeschriftung: die wiederholt sich zwischen den Bereichen ("Heute" gibt es in
   * NRW und in COSMO), das Präfix nennt beides eindeutig. Die Tabelle führt NRW, COSMO
   * und je Lokalzeit-Stadt ({@link LOKALZEIT_STAEDTE}) die sechs Zeiträume Heute … 3.
   * Aussichtentag sowie die Prognose. "Lokalzeit am Samstag" fehlt (noch): ihr Reiter
   * "KI-Texte" ist anders gebaut (keine Heute- und keine Schlagzeilen-Box, eine gemeinsame
   * Aussichten-Box), ihre Knöpfe bleiben ohne Funktion.
   *
   * `gebiet` ist der Gebietsname in der Anfrage: bei den Lokalzeiten der Stadtname statt der
   * Reiterbeschriftung "Lokalzeiten" (das AI-Modul erkennt daran die Region und lädt ihre
   * Formulierungsbeispiele). `auswahl` ist eine Besonderheit der Lokalzeit-Prognose: sie
   * sendet nur die Boxen mit `kiFeld` (Wind, Regen, Sonne – keine Umschalter, keine
   * Temperaturen).
   *
   * ⚠️ **Ein Ziel kann mehrere Präfixe haben.** COSMO hat für die Aussichten nur **eine**
   * Textbox ("Aussichten (2 bis 3 Tage)"), gefüllt aus allen drei Aussichten-Reitern
   * zusammen. Der Knopf in jedem dieser Reiter schickt deshalb die Karten aller drei
   * ({@link kartenFuerZiel}); die grüne Box bleibt trotzdem die des eigenen Reiters
   * ({@link reiterUmfangFuer}).
   */
  /**
   * Die Textbox des Reiters "Heute" – der einzige Reiter, der zusätzlich die Schlagzeile
   * erzeugt. Als Konstante, weil die `DocumentDetailComponent` darauf prüft.
   */
  public static readonly ZIEL_HEUTE = 'wdr.kitexte.heute';

  /** BOX-Name der Lokalzeit-Prognose – das AI-Modul kennt ihn unter genau diesem Namen. */
  public static readonly BOX_WIND_REGEN_SONNE = 'Wind, Regen, Sonne';

  /**
   * Die Lokalzeit-Städte: Stadt-Segment der Karten-`containerId`, Kürzel der Ziel-Textboxen
   * und der Gebietsname für die Anfrage. Die Textboxen heißen so, wie der Rest-Service die
   * Platzhalter des Templates auflöst (Wuppertal ist `wu`, nicht `w`):
   * - `wdr.lokalzeiten.<kürzel>.lokalzeit<stadt>.{schlagzeile,heute,nacht,morgen}`
   * - `wdr.lokalzeiten.<kürzel>.weitereaussichten.wochentag.{1,2,3}`
   * - `wdr.lokalzeiten.<kürzel>.tvvorhersage.windregensonne` (Prognose)
   *
   * Der Gebietsname trägt die Umlaute ("Köln", "Düsseldorf", "Münster"): das AI-Modul
   * erkennt die Lokalzeit-Region an genau dieser Schreibweise
   * (`RegionFormulationService.REGIONEN`). "Lokalzeit am Samstag" fehlt, siehe {@link ZIELE}.
   */
  private static readonly LOKALZEIT_STAEDTE: {stadt: string; kuerzel: string; gebiet: string}[] = [
    {stadt: 'aachen', kuerzel: 'ac', gebiet: 'Aachen'},
    {stadt: 'bonn', kuerzel: 'bn', gebiet: 'Bonn'},
    {stadt: 'koeln', kuerzel: 'k', gebiet: 'Köln'},
    {stadt: 'duesseldorf', kuerzel: 'd', gebiet: 'Düsseldorf'},
    {stadt: 'duisburg', kuerzel: 'du', gebiet: 'Duisburg'},
    {stadt: 'essen', kuerzel: 'e', gebiet: 'Essen'},
    {stadt: 'dortmund', kuerzel: 'do', gebiet: 'Dortmund'},
    {stadt: 'muenster', kuerzel: 'ms', gebiet: 'Münster'},
    {stadt: 'bielefeld', kuerzel: 'bi', gebiet: 'Bielefeld'},
    {stadt: 'siegen', kuerzel: 'si', gebiet: 'Siegen'},
    {stadt: 'wuppertal', kuerzel: 'wu', gebiet: 'Wuppertal'}
  ];

  private static readonly ZIELE: {
    praefixe: string[]; ziel: string; box: string; auswahl?: KiWertauswahl; gebiet?: string
  }[] = [
    {praefixe: ['wdr.grafik.heute-'], ziel: KiGrafikService.ZIEL_HEUTE, box: 'Heute'},
    {praefixe: ['wdr.grafik.nacht-'], ziel: 'wdr.kitexte.nacht', box: 'Nacht'},
    {praefixe: ['wdr.grafik.morgen-'], ziel: 'wdr.kitexte.morgen', box: 'Morgen'},
    {praefixe: ['wdr.grafik.1-aussichtentag-'], ziel: 'wdr.kitexte.aussichten.tag1', box: '1.Aussichtentag'},
    {praefixe: ['wdr.grafik.2-aussichtentag-'], ziel: 'wdr.kitexte.aussichten.tag2', box: '2.Aussichtentag'},
    {praefixe: ['wdr.grafik.3-aussichtentag-'], ziel: 'wdr.kitexte.aussichten.tag3', box: '3.Aussichtentag'},
    // COSMO (NRW, Bremen, Berlin): eigene Textboxen, eigene BOX-Namen und damit eigene
    // Regeln im AI-Modul. Die Aussichten fassen alle drei Aussichten-Reiter zusammen.
    {praefixe: ['wdr.cosmo.heute-'], ziel: 'wdr.cosmo.cosmovorhersagenrwbremenberlin.heute', box: 'COSMO Heute'},
    {praefixe: ['wdr.cosmo.nacht-'], ziel: 'wdr.cosmo.cosmovorhersagenrwbremenberlin.nacht', box: 'COSMO Nacht'},
    {praefixe: ['wdr.cosmo.morgen-'], ziel: 'wdr.cosmo.cosmovorhersagenrwbremenberlin.morgen', box: 'COSMO Morgen'},
    {
      praefixe: ['wdr.cosmo.1-aussichtentag-', 'wdr.cosmo.2-aussichtentag-', 'wdr.cosmo.3-aussichtentag-'],
      ziel: 'wdr.cosmo.cosmovorhersagenrwbremenberlin.aussichten',
      box: 'COSMO Aussichten'
    },
    // Lokalzeit: aus der Prognose-Karte (Reiter "Tendenzen/Prognosen") in die TV-Vorhersage-Box
    // "Wind, Regen, Sonne" der Stadt. Nur die Boxen mit `kiFeld`, Gebiet = Stadtname.
    ...KiGrafikService.LOKALZEIT_STAEDTE.map(stadt => ({
      praefixe: [`wdr.lokalzeit.${stadt.stadt}.prognose.`],
      ziel: `wdr.lokalzeiten.${stadt.kuerzel}.tvvorhersage.windregensonne`,
      box: KiGrafikService.BOX_WIND_REGEN_SONNE,
      auswahl: 'kifelder' as KiWertauswahl,
      gebiet: stadt.gebiet
    })),
    // Lokalzeit: die sechs Zeiträume je Stadt in die Textboxen ihres Reiters "KI-Texte".
    // Dieselben BOX-Namen wie in NRW – im AI-Modul gelten dieselben Box-Regeln, die
    // Lokalzeit-Region erkennt es am Gebietsnamen.
    ...KiGrafikService.LOKALZEIT_STAEDTE.flatMap(stadt => {
      const karte = `wdr.lokalzeit.${stadt.stadt}.`;
      const box = `wdr.lokalzeiten.${stadt.kuerzel}.lokalzeit${stadt.stadt}.`;
      const aussichten = `wdr.lokalzeiten.${stadt.kuerzel}.weitereaussichten.wochentag.`;
      return [
        {praefixe: [karte + 'heute-'], ziel: box + 'heute', box: 'Heute', gebiet: stadt.gebiet},
        {praefixe: [karte + 'nacht-'], ziel: box + 'nacht', box: 'Nacht', gebiet: stadt.gebiet},
        {praefixe: [karte + 'morgen-'], ziel: box + 'morgen', box: 'Morgen', gebiet: stadt.gebiet},
        {praefixe: [karte + '1-aussichtentag-'], ziel: aussichten + '1', box: '1.Aussichtentag', gebiet: stadt.gebiet},
        {praefixe: [karte + '2-aussichtentag-'], ziel: aussichten + '2', box: '2.Aussichtentag', gebiet: stadt.gebiet},
        {praefixe: [karte + '3-aussichtentag-'], ziel: aussichten + '3', box: '3.Aussichtentag', gebiet: stadt.gebiet},
      ];
    })
  ];

  /**
   * Die Schlagzeilen: je eine für NRW und je Lokalzeit-Stadt. Eine Schlagzeile gehört zu
   * keinem einzelnen Reiter, sondern fasst **alle** Zeiträume ihres Bereichs zusammen
   * (`praefixe`) – ausgelöst wird sie aber nur aus dem Reiter "Heute" (`heute`): aus einem
   * Aussichtentag heraus erzeugt, ersetzte sie die Schlagzeile von heute durch eine von
   * übermorgen. COSMO hat keine.
   */
  private static readonly SCHLAGZEILEN: {ziel: string; heute: string; nacht: string; praefixe: string[]; gebiet?: string}[] = [
    {
      ziel: 'wdr.kitexte.schlagzeile',
      heute: 'wdr.kitexte.heute',
      nacht: 'wdr.kitexte.nacht',
      praefixe: ['heute-', 'nacht-', 'morgen-', '1-aussichtentag-', '2-aussichtentag-', '3-aussichtentag-']
        .map(zeitraum => 'wdr.grafik.' + zeitraum)
    },
    ...KiGrafikService.LOKALZEIT_STAEDTE.map(stadt => ({
      ziel: `wdr.lokalzeiten.${stadt.kuerzel}.lokalzeit${stadt.stadt}.schlagzeile`,
      heute: `wdr.lokalzeiten.${stadt.kuerzel}.lokalzeit${stadt.stadt}.heute`,
      nacht: `wdr.lokalzeiten.${stadt.kuerzel}.lokalzeit${stadt.stadt}.nacht`,
      praefixe: ['heute-', 'nacht-', 'morgen-', '1-aussichtentag-', '2-aussichtentag-', '3-aussichtentag-']
        .map(zeitraum => `wdr.lokalzeit.${stadt.stadt}.${zeitraum}`),
      gebiet: stadt.gebiet
    }))
  ];

  /**
   * Die Schlagzeile von NRW: sie gehört zu keinem einzelnen Reiter, sondern fasst **alle**
   * zusammen. Gefüllt wird sie deshalb aus einer zweiten Anfrage über alle Zeiträume. Die
   * Lokalzeit-Städte haben je eine eigene, siehe {@link SCHLAGZEILEN}.
   */
  public static readonly ZIEL_SCHLAGZEILE = 'wdr.kitexte.schlagzeile';

  /** Ist das eine Schlagzeilen-Textbox (NRW oder eine Lokalzeit-Stadt)? */
  public static istSchlagzeile(zielId: string | null | undefined): boolean {
    return KiGrafikService.SCHLAGZEILEN.some(s => s.ziel === zielId);
  }

  /**
   * Die Schlagzeile, die ein Knopfdruck für diese Textbox zusätzlich erzeugt – nur für die
   * Heute-Box eines Bereichs, sonst null.
   */
  public static schlagzeileZu(zielId: string | null | undefined): string | null {
    return KiGrafikService.SCHLAGZEILEN.find(s => s.heute === zielId)?.ziel ?? null;
  }

  /**
   * Die Schlagzeile, die der Reiter "Nacht" **auf Wunsch** zusätzlich erzeugt (Schalter
   * "Schlagzeile erzeugen" der Leiste oben, `SchlagzeileSchalterService`, Oktober 2026) – nur
   * für die Nacht-Box eines Bereichs, sonst null. Anders als aus "Heute" wird sie nicht aus
   * allen Zeiträumen gespeist, sondern allein aus den Nacht-Karten (`kartenFuerZiel`).
   */
  public static nachtSchlagzeileZu(zielId: string | null | undefined): string | null {
    return KiGrafikService.SCHLAGZEILEN.find(s => s.nacht === zielId)?.ziel ?? null;
  }

  /**
   * Der Name der Ziel-Textbox, wie er als `BOX:` im Prompt steht ("Schlagzeile", "Heute",
   * …, "3.Aussichtentag", bei COSMO "COSMO Heute" … "COSMO Aussichten") – oder null für
   * eine Box außerhalb der {@link ZIELE}-Tabelle.
   *
   * Bewusst fest im Client und nicht die Beschriftung aus dem Template ("1. Aussichtentag
   * (= Wochentag)"): das AI-Modul lädt über diesen Namen die Regeln der Box aus seiner
   * Datenbank und muss ihn deshalb verlässlich wiedererkennen.
   */
  public static boxNameFuer(zielId: string | null | undefined): string | null {
    if (KiGrafikService.istSchlagzeile(zielId)) {
      return 'Schlagzeile';
    }
    return KiGrafikService.ZIELE.find(z => z.ziel === zielId)?.box ?? null;
  }

  /**
   * Welche Werte die Anfrage für diese Textbox trägt: `'alle'` für die Wetter-Texte,
   * `'kifelder'` für die Lokalzeit-Prognose (nur Boxen mit `kiFeld`, ohne Umschalter).
   * Ein Ziel mit `'kifelder'` kennt keine "angehakten Zeilen" – seine Boxen haben keine
   * Umschalter, die Zeilen-Auswahl des Reiter-Knopfs entfällt dort.
   */
  public static auswahlFuer(zielId: string | null | undefined): KiWertauswahl {
    return KiGrafikService.ZIELE.find(z => z.ziel === zielId)?.auswahl ?? 'alle';
  }

  /** Gebietsname, den die Anfrage für dieses Ziel tragen soll, oder null für die Reiterbeschriftung. */
  public static gebietFuer(zielId: string | null | undefined): string | null {
    return KiGrafikService.ZIELE.find(z => z.ziel === zielId)?.gebiet
      ?? KiGrafikService.SCHLAGZEILEN.find(s => s.ziel === zielId)?.gebiet
      ?? null;
  }

  /**
   * Die Textbox-Typen, in die ein KI-Text geschrieben werden darf. Die Aussichten-Boxen der
   * Lokalzeiten sind `TextAreaWithDateCaption` (Überschrift mit Datum) – im Rest-Service
   * eine Unterklasse von `TextArea`, mit `maxChars` und denselben KI-Knöpfen.
   */
  private static readonly TEXTBOX_TYPEN = ['TextArea', 'TextAreaWithDateCaption'];

  /** Trennzeichen zwischen Box und Umschalter im Wert-Datensatz (wie im `ImageComponent`). */
  private static readonly UMSCHALTER_TRENNER = '::';

  /**
   * Präfix der reservierten Schlüssel, unter denen die KI-Antworten in `image.values`
   * liegen. Ein **einzelner** Doppelpunkt kommt in keinem Box-Schlüssel vor (Umschalter
   * benutzen `::`), die Werte können sich also nicht in die Wege kommen.
   *
   * ⚠️ Dasselbe Präfix steht in `ImageComponent.WERT_PRAEFIX_FREMD` – wer es hier
   * ändert, muss es dort mitändern, sonst verwirft die Bild-Komponente die Antworten
   * beim nächsten Spiegeln ihres Wert-Caches.
   */
  public static readonly ABLAGE_PRAEFIX = 'ki:';

  /**
   * Schlüssel der Reihenfolge-Nummer einer Box in `image.values`: `ki:order:<boxId>` = "3".
   * Geschrieben vom `KiReihenfolgeService`, gelesen hier beim Bauen der Anfrage – aus dem
   * Dokument, nicht aus dem Service, damit auch nie gerenderte Karten ihre Nummer mitbringen.
   */
  public static readonly ORDER_PRAEFIX = KiGrafikService.ABLAGE_PRAEFIX + 'order:';

  /**
   * Der Schalter "Temp" einer Karte: steht er auf `'1'`, gehen die Temperaturen **dieser
   * Karte** in die Anfrage, auf `'0'` nicht – unabhängig von den gelben Umschaltern. Ohne
   * Eintrag gilt der Standard der Zeile ({@link temperaturStandard}): in den "ganz"-Zeilen
   * an, sonst aus. Deshalb wird auch "aus" ausdrücklich als `'0'` gespeichert – sonst sprünge
   * ein bewusst ausgeschalteter Schalter in einer "ganz"-Zeile wieder auf an.
   *
   * Er liegt in `image.values` unter dem fremden Präfix `ki:`: so speichert er mit dem
   * Datensatz, das `ImageComponent` reicht ihn beim Spiegeln durch, und weder "leeren" noch
   * die Box-Zählung sehen ihn als Box-Wert.
   */
  public static readonly TEMP_SCHALTER = KiGrafikService.ABLAGE_PRAEFIX + 'temp';

  /**
   * Die Verbindungs-Pfeile einer Karte in `image.values`: `ki:pfeile` = `"a>b;b>c"` – je
   * Eintrag die Box-Id, von der der Pfeil ausgeht, und die Box-Id, auf die er zeigt
   * (Oktober 2026). Gezogen vom Halbkreis-Griff unter einer angehakten Box im
   * `ImageComponent`; eine Box hat höchstens einen ausgehenden Pfeil, Pfeile bleiben
   * innerhalb ihrer Karte. Wie `ki:temp` ein fremder Schlüssel: speichert mit, zählt nicht
   * als Box-Wert. Im JSON der Anfrage wird daraus je Quell-Region {@link PFEIL_ICON} bzw.
   * {@link PFEIL_WIND} mit dem Regionsnamen des Ziels.
   */
  public static readonly PFEILE_SCHLUESSEL = KiGrafikService.ABLAGE_PRAEFIX + 'pfeile';
  /** Trenner innerhalb eines Pfeils (`von>nach`) und zwischen den Pfeilen (`;`). */
  private static readonly PFEIL_TRENNER = '>';
  private static readonly PFEIL_LISTE_TRENNER = ';';
  /**
   * Feld im JSON: die Region, zu der der Pfeil vom Wetter-Symbol dieser Region führt
   * (Symbol- und Kombi-Boxen) bzw. vom Wind (Wind-Boxen). Das Modell soll die verbundenen
   * Regionen als geografischen Weg nennen ("vom südlichen Rheinland übers Siegerland bis zur
   * Warburger Börde") – Regel 18 des Haupt-Prompts.
   */
  public static readonly PFEIL_ICON = 'pfeil_icon';
  public static readonly PFEIL_WIND = 'pfeil_wind';

  /** Die Pfeile einer Karte aus `image.values` (Quell-Box-Id → Ziel-Box-Id), leer wenn keine. */
  public static pfeileVon(bild: Image | null | undefined): Map<string, string> {
    const pfeile = new Map<string, string>();
    const text = (bild?.values ?? {})[KiGrafikService.PFEILE_SCHLUESSEL];
    for (const eintrag of (text ?? '').split(KiGrafikService.PFEIL_LISTE_TRENNER)) {
      const [von, nach] = eintrag.split(KiGrafikService.PFEIL_TRENNER);
      if (von && nach && von !== nach) {
        pfeile.set(von, nach);
      }
    }
    return pfeile;
  }

  /** Die Pfeile als Wert für `ki:pfeile`, oder undefined wenn keine da sind. */
  public static pfeileAlsText(pfeile: ReadonlyMap<string, string>): string | undefined {
    if (pfeile.size === 0) {
      return undefined;
    }
    return [...pfeile.entries()]
      .map(([von, nach]) => von + KiGrafikService.PFEIL_TRENNER + nach)
      .join(KiGrafikService.PFEIL_LISTE_TRENNER);
  }

  /**
   * Darf diese Box einen Verbindungs-Pfeil tragen? Symbol-, Kombi- und Wind-Boxen – bei
   * einer Kombi-Box meint der Pfeil nur das Symbol, bei einer Wind-Box nur den Wind.
   * Warnschilder, Temperatur- und Wert-Boxen nicht.
   */
  public static kannPfeilTragen(box: ImageBox): boolean {
    return box.type === 'icon' || box.type === 'tempicon' || box.type === 'maxminicon' || box.type === 'wind';
  }

  /**
   * Karten mit Temp-Schalter: die NRW-Grafiken (`wdr.grafik.`) mit Temperatur-Boxen, also
   * "feine Unterteilung" und "sehr feine Unterteilung". COSMO und die Lokalzeiten haben
   * keinen – deren Temperaturen gehen wie bisher immer mit.
   */
  private static readonly TEMP_SCHALTER_PRAEFIX = 'wdr.grafik.';

  /** Die Umfang-Arten, unter denen abgelegt wird: `ki:reiter` und `ki:zeile`. */
  private static readonly ARTEN: GrafikUmfangArt[] = ['reiter', 'zeile'];

  /**
   * Die Beaufort-Skala des Mittelwinds, wie sie das Modell benennt.
   *
   * Sie steht hier nicht zur Zierde: aus ihr ergibt sich, **ab wann Sturm ist** – und das
   * entscheidet, ob ein Windwert in die Schlagzeile darf. Der Index ist die Bft-Zahl.
   */
  private static readonly MITTELWIND = [
    'Windstille, windstill',      // 0
    'kaum spürbarer Wind',        // 1
    'leichte Brise',              // 2
    'schwacher Wind',             // 3
    'mäßiger Wind',               // 4
    'frischer Wind',              // 5
    'starker Wind',               // 6
    'starker bis stürmischer Wind', // 7
    'stürmischer Wind',           // 8
    'Sturm',                      // 9  <- ab hier Sturm
    'schwerer Sturm',             // 10
    'orkanartiger Sturm',         // 11
    'Orkan'                       // 12
  ];

  /** Dieselbe Skala für die Böen; unterhalb von 5 hat sie keine eigene Benennung. */
  private static readonly BOEEN = new Map<number, string>([
    [5, 'frische Böen'],
    [6, 'starke Böen'],
    [7, 'starke bis stürmische Böen'],
    [8, 'stürmische Böen'],
    [9, 'Sturmböen'],            // <- ab hier Sturm
    [10, 'schwere Sturmböen'],
    [11, 'orkanartige Böen'],
    [12, 'Orkanböen']
  ]);

  /**
   * Ab dieser Bft-Zahl ist **Sturm** – für Mittelwind und Böen dieselbe Grenze.
   *
   * Sie folgt der Benennung der Skala: 8 ist "stürmischer Wind" bzw. "stürmische Böen",
   * das Substantiv **Sturm** beginnt bei 9 ("Sturm", "Sturmböen"). Wer die Schlagzeile
   * schon bei stürmischem Wind warnen lassen will, setzt hier 8 – eine Zeile, und die
   * Skalen oben belegen, was das bedeutet.
   */
  private static readonly STURM_AB = 9;

  /** Karten von COSMO – dort wird der Wind verdichtet, siehe {@link verdichteCosmoWind}. */
  private static readonly COSMO_PRAEFIX = 'wdr.cosmo.';

  /**
   * Ab dieser Bft-Zahl (Böen oder Mittelwind, der höhere zählt) kommt Wind in eine
   * COSMO-Anfrage. Darunter erwähnt der COSMO-Text keinen Wind.
   */
  public static readonly COSMO_WIND_AB = 6;

  /**
   * Die COSMO-Regionen außerhalb von NRW, wie sie auf der Wind-Karte heißen. Jede andere
   * Wind-Box der COSMO-Karte liegt in NRW.
   */
  private static readonly COSMO_REGIONEN_AUSSER_NRW = ['Bremen', 'Berlin'];

  /** Name des verdichteten NRW-Wind-Eintrags im JSON. */
  private static readonly COSMO_NRW = 'NRW';

  /** Zusatz-Schlüssel der Kombi-Boxen (wie im `ImageComponent`). */
  private static readonly SUFFIX_ICON = '.icon';
  private static readonly SUFFIX_MIN = '.min';
  private static readonly SUFFIX_BOEEN = '.boeen';
  private static readonly SUFFIX_DIR = '.dir';

  /**
   * Baut den Anfrage-Körper aus den angehakten Boxen des Dokuments.
   *
   * Läuft über den ganzen Baum, nicht nur über den offenen Reiter: die Reiter werden
   * erst beim Öffnen gerendert (`matTabContent`), die Werte liegen aber unabhängig davon
   * im Dokument (`image.values` / `section.values`) – gesammelt wird also aus dem
   * Datenbestand und nicht aus der Anzeige.
   *
   * @param kartenIds Umfang: `containerId`s der Karten, die gelesen werden dürfen.
   *   `null` oder leer = **alle** Karten, also der Knopf der Kopfleiste; eine Liste
   *   beschränkt auf eine Zeile, also der Knopf an deren Überschrift.
   * @param auswahl Welche Werte mitgehen – siehe {@link KiWertauswahl}. Die Schlagzeile
   *   lässt Temperaturen und Wind weg, außer bei Sturm.
   * @param nurAngehakteZeilen Nur Zeilen (Zeiträume), in denen mindestens eine Box den
   *   gelben Umschalter trägt – samt deren Temperaturen und Wind. Alle anderen Zeilen
   *   fallen komplett weg. So arbeitet der Knopf **oben** am Reiter: der Redakteur wählt
   *   über die Umschalter, welche Zeilen des Reiters in den Text gehören, statt dass jede
   *   Zeile mit gefüllten Messwerten mitgeht.
   */
  public baueAnfrage(document: DocumentModel, rfaKuerzel: string,
                     kartenIds: string[] | null = null,
                     auswahl: KiWertauswahl = 'alle',
                     nurAngehakteZeilen = false,
                     gebiet: string | null = null): GrafikKiErgebnis {
    const umfang = kartenIds && kartenIds.length > 0 ? new Set(kartenIds) : null;
    const gebiete: KiGebiete = {};
    let boxen = 0;
    // Erst alle Reiter einsammeln, dann die Reihenfolge-Nummern über die ganze Anfrage
    // lückenlos ab 1 vergeben – das Dokument zählt kartenübergreifend, die Anfrage sieht
    // nur einen Ausschnitt.
    const gesammelt: {reiter: Tab; zeitraeume: Map<string, KiBoxEintrag[]>}[] = [];
    for (const reiter of document.tabs ?? []) {
      const zeitraeume = new Map<string, KiBoxEintrag[]>();
      const angehakteZeitraeume = new Set<string>();
      this.sammleBilder(reiter.containerList ?? [], zeitraeume, umfang, auswahl,
        angehakteZeitraeume);
      if (nurAngehakteZeilen) {
        for (const zeitraum of [...zeitraeume.keys()]) {
          if (!angehakteZeitraeume.has(zeitraum)) {
            zeitraeume.delete(zeitraum);
          }
        }
      }
      KiGrafikService.verdichteCosmoWind(zeitraeume);
      gesammelt.push({reiter, zeitraeume});
    }
    KiGrafikService.nummeriereNeu(gesammelt.map(g => g.zeitraeume));
    for (const {reiter, zeitraeume} of gesammelt) {
      const zeiten = KiGrafikService.alsZeiten(zeitraeume);
      if (zeiten.length === 0) {
        continue;
      }
      boxen += KiGrafikService.zaehle(zeitraeume);
      // Das Gebiet ist die Beschriftung des obersten Reiters – außer ein Ziel bringt ein
      // eigenes mit (Lokalzeit: der Stadtname statt "Lokalzeiten").
      gebiete[gebiet ?? KiGrafikService.gebietsname(reiter.caption)] = {zeiten} as KiGebiet;
    }
    if (boxen === 0) {
      if (auswahl === 'kifelder') {
        return {boxen: 0, grund: 'In der Prognose ist kein Wert für Wind, Regen oder Sonne '
          + 'gefüllt – erst "MSWR-MOS laden".'};
      }
      if (auswahl === 'schlagzeile') {
        return {boxen: 0, grund: 'Für die Schlagzeile ist kein angehaktes Symbol gefüllt '
          + '(und kein Sturm gemeldet).'};
      }
      if (nurAngehakteZeilen) {
        return {boxen: 0, grund: 'In keiner Zeile dieses Reiters ist ein Symbol mit dem '
          + 'gelben Umschalter angehakt und gefüllt.'};
      }
      return {boxen: 0, grund: umfang
        ? 'In dieser Zeile ist keine Temperatur, kein Wind und kein angehaktes Symbol gefüllt.'
        : 'Es ist keine Temperatur, kein Wind und kein angehaktes Symbol gefüllt.'};
    }
    return {boxen, anfrage: {rfas: [{[(rfaKuerzel || 'wdr').toUpperCase()]: gebiete}]}};
  }

  /**
   * Die Textbox, in die die Antwort dieses Umfangs gehört – oder null, wenn der Umfang
   * zu keinem Zeitraum der {@link ZIELE}-Tabelle passt.
   *
   * Entschieden wird an der **ersten Karte** des Umfangs: alle Karten eines Reiters und
   * erst recht die einer Zeile gehören zum selben Zeitraum.
   */
  public zielFuer(umfang: GrafikUmfang): string | null {
    const erste = umfang.kartenIds?.[0];
    if (!erste) {
      return null;
    }
    return KiGrafikService.ZIELE.find(z => z.praefixe.some(p => erste.startsWith(p)))?.ziel
      ?? null;
  }

  /**
   * Die Karten, aus denen der Text dieser Textbox entsteht – bei COSMO "Aussichten" die
   * aller drei Aussichten-Reiter, sonst die des einen Reiters. Leer für eine Box außerhalb
   * der {@link ZIELE}-Tabelle.
   */
  public kartenFuerZiel(document: DocumentModel, zielId: string): string[] {
    const eintrag = KiGrafikService.ZIELE.find(ziel => ziel.ziel === zielId);
    return eintrag ? this.kartenMitPraefix(document, eintrag.praefixe) : [];
  }

  /**
   * Der Umfang des **Reiters**, zu dem die erste Karte dieses Umfangs gehört – für den
   * KI-Knopf an einer Zeile, dessen Antwort in der grünen Box oben erscheint.
   *
   * Anders als {@link umfangFuerZiel} nur der eine Reiter, auch wenn die Textbox aus
   * mehreren gefüllt wird (COSMO "Aussichten"): die grüne Box steht unter der Leiste
   * **dieses** Reiters, und ihr Schlüssel muss mit `umfangDesReiters()` übereinstimmen.
   */
  public reiterUmfangFuer(document: DocumentModel, umfang: GrafikUmfang): GrafikUmfang | null {
    const erste = umfang.kartenIds?.[0];
    const eintrag = erste
      ? KiGrafikService.ZIELE.find(z => z.praefixe.some(p => erste.startsWith(p)))
      : undefined;
    const praefix = eintrag?.praefixe.find(p => erste!.startsWith(p));
    if (!eintrag || !praefix) {
      return null;
    }
    const ids = this.kartenMitPraefix(document, [praefix]);
    return ids.length > 0
      ? new GrafikUmfang(ids, 'reiter', this.zielName(document, eintrag.ziel))
      : null;
  }

  /** Die `containerId`s aller Karten mit einem dieser Präfixe, in Dokumentreihenfolge. */
  private kartenMitPraefix(document: DocumentModel, praefixe: string[]): string[] {
    const ids: string[] = [];
    this.durchlaufeKarten(document.tabs ?? [], karte => {
      const id = karte.containerId;
      if (id && praefixe.some(p => id.startsWith(p))) {
        ids.push(id);
      }
    });
    return ids;
  }

  /**
   * Der **Rückweg** der {@link ZIELE}-Tabelle: welcher Reiter-Umfang füllt diese Textbox?
   *
   * Damit lässt sich die Anfrage, die eine Box gefüllt hat, aus der Box allein
   * rekonstruieren – das ist die Grundlage von "Erneut Laden". Es gibt keinen
   * gespeicherten Anfrage-Verlauf und braucht auch keinen: der Umfang eines Reiters ergibt
   * sich aus dem `containerId`-Präfix seiner Karten.
   *
   * ⚠️ Es kommt immer der **Reiter**-Umfang zurück, nie der einer einzelnen Zeile. Beide
   * schreiben in dieselbe Textbox, aus der Box ist also nicht ablesbar, welcher der beiden
   * sie zuletzt gefüllt hat – und der Reiter ist die vollständigere Antwort. Die
   * Karten-Reihenfolge ist dieselbe wie in `umfangDesReiters()`, der Schlüssel damit auch:
   * die erneute Antwort landet in derselben grünen Box.
   *
   * Für die Schlagzeile liefert die Methode `null` – sie hängt an keinem Reiter, ihr
   * Umfang ist {@link kartenAllerZeitraeume}.
   *
   * Bei COSMO "Aussichten" umfasst er die Karten **aller drei** Aussichten-Reiter; sein
   * Schlüssel beginnt mit der ersten Karte des 1. Aussichtentags, die erneute Antwort
   * erscheint also in dessen grüner Box.
   */
  public umfangFuerZiel(document: DocumentModel, zielId: string): GrafikUmfang | null {
    const ids = this.kartenFuerZiel(document, zielId);
    return ids.length > 0
      ? new GrafikUmfang(ids, 'reiter', this.zielName(document, zielId))
      : null;
  }

  /**
   * Beschriftung einer Ziel-Textbox für Meldungen, ohne ihren Klammer-Zusatz:
   * "1. Aussichtentag (= Wochentag)" wird zu "1. Aussichtentag".
   */
  private zielName(document: DocumentModel, zielId: string): string {
    const beschriftung = this.findeContainer(document.tabs ?? [], zielId)?.caption ?? '';
    const klammer = beschriftung.indexOf(' (');
    return klammer > 0 ? beschriftung.slice(0, klammer) : beschriftung;
  }

  /**
   * Die Zeichengrenze einer Textbox, wie sie im Server-Template steht
   * (`TextArea.maxChars`) – oder null, wenn es die Box nicht gibt oder sie keine nennt.
   *
   * Sie geht als `MAX_CHARS` in die Anfrage: der Endpunkt `chat/` reicht den Rumpf eins
   * zu eins durch und ergänzt nichts, das Modell muss die Grenze also von uns hören.
   * Geraten wird sie nicht – sie steht im Template (Schlagzeile 160, Heute/Nacht/Morgen
   * 500, Aussichtentage 165) und ändert sich dort, nicht hier.
   */
  public maxCharsVon(document: DocumentModel, containerId: string): number | null {
    const ziel = this.findeContainer(document.tabs ?? [], containerId);
    if (!ziel || !KiGrafikService.TEXTBOX_TYPEN.includes(ziel.containerType)) {
      return null;
    }
    return (ziel as {maxChars?: number | null}).maxChars ?? null;
  }

  /**
   * Schreibt einen KI-Text in eine Textbox und meldet dessen Beschriftung zurück –
   * bzw. null, wenn es die Box nicht gibt.
   *
   * Die Zuweisung an `content` genügt: das Textfeld hängt mit `[(ngModel)]` daran und
   * der Reiter läuft auf `Eager`-Change-Detection — derselbe Weg, den der
   * Videotext-Knopf schon nimmt. Dass der Reiter "KI-Texte" beim Schreiben womöglich
   * noch nie geöffnet war, ist unerheblich: geschrieben wird in den Dokumentbaum, und
   * die Textbox liest ihn beim Aufbau.
   */
  public schreibeText(document: DocumentModel, containerId: string, text: string): string | null {
    const ziel = this.findeContainer(document.tabs ?? [], containerId);
    if (!ziel || !KiGrafikService.TEXTBOX_TYPEN.includes(ziel.containerType)) {
      console.warn('KI-Ergebnis konnte nicht zugeordnet werden: kein TextArea mit containerId '
        + containerId);
      return null;
    }
    (ziel as {content?: string}).content = text;
    return ziel.caption || containerId;
  }

  /**
   * Die Karten **aller** Zeiträume eines Bereichs – die Grundlage seiner Schlagzeile.
   *
   * Nicht einfach "das ganze Dokument": {@link SCHLAGZEILEN} nennt je Schlagzeile genau die
   * Zeiträume ihres Bereichs – für NRW die sechs `wdr.grafik.`-Zeiträume, für eine
   * Lokalzeit-Stadt deren sechs. COSMO, die anderen Städte und die Prognose bleiben draußen.
   *
   * @param schlagzeile die Ziel-Textbox; ohne Angabe die von NRW
   */
  public kartenAllerZeitraeume(document: DocumentModel,
                               schlagzeile: string = KiGrafikService.ZIEL_SCHLAGZEILE): string[] {
    const praefixe = KiGrafikService.SCHLAGZEILEN.find(s => s.ziel === schlagzeile)?.praefixe ?? [];
    return this.kartenMitPraefix(document, praefixe);
  }

  /**
   * Läuft den Reiter-Teilbaum ab und sammelt die angehakten Boxen jedes Bildes ein.
   *
   * `umfang` ist die Menge erlaubter `containerId`s oder null für alle. Gefiltert wird
   * hier und nicht am Reiter, weil eine Zeile mitten im Baum liegt – der Rest des
   * Reiters wird einfach übersprungen.
   */
  private sammleBilder(container: Container[], zeitraeume: Map<string, KiBoxEintrag[]>,
                       umfang: ReadonlySet<string> | null, auswahl: KiWertauswahl,
                       angehakt: Set<string>): void {
    for (const kind of container) {
      if (kind.containerType === 'Image' && (!umfang || umfang.has(kind.containerId))) {
        this.sammleBild(kind as Image, zeitraeume, auswahl, angehakt);
      }
      this.sammleBilder(kind.containerList ?? [], zeitraeume, umfang, auswahl, angehakt);
    }
  }

  /**
   * Die angehakten Boxen einer Karte.
   *
   * Zwei Fälle: eine Karte ohne Sidebar hat einen Zeitraum – den ihrer Zeile – und ihre
   * Werte stehen in `image.values`. Eine Karte mit Sidebar (die Lokalzeit-Grafiken) hat
   * einen Zeitraum je Abschnitt, und die Werte stehen in `section.values`.
   *
   * ⚠️ Die **Umschalter** liegen in beiden Fällen in `image.values`: sie gelten für die
   * Box und nicht für einen Abschnitt (so legt sie das `ImageComponent` ab).
   */
  private sammleBild(bild: Image, zeitraeume: Map<string, KiBoxEintrag[]>,
                     auswahl: KiWertauswahl, angehakt: Set<string>): void {
    const umschalter = bild.values ?? {};
    const abschnitte = bild.sections ?? [];
    if (abschnitte.length === 0) {
      this.sammleBoxen(bild, bild.boxes ?? [], bild.values ?? {}, umschalter,
        KiGrafikService.zeitAus(bild.containerId), zeitraeume, auswahl, angehakt,
        KiGrafikService.temperaturFreigegeben(bild),
        KiGrafikService.hatTemperaturSchalter(bild));
      return;
    }
    for (const abschnitt of abschnitte) {
      if (abschnitt.graph) {
        continue;   // Diagramm-Abschnitte haben keine Boxen
      }
      this.sammleBoxen(bild, KiGrafikService.boxenVon(bild, abschnitt), abschnitt.values ?? {},
        umschalter, KiGrafikService.slug(KiGrafikService.abschnittsKey(abschnitt)), zeitraeume,
        auswahl, angehakt, true, false);
    }
  }

  /**
   * Trägt diese Karte den Schalter "Temp"? Nur NRW-Grafiken ohne Sidebar, die
   * Temperatur-Boxen haben – das sind "feine" und "sehr feine Unterteilung". Dieselbe
   * Prüfung entscheidet im `ImageComponent`, ob der Schalter gezeigt wird.
   */
  public static hatTemperaturSchalter(bild: Image | null | undefined): boolean {
    return !!bild?.containerId?.startsWith(KiGrafikService.TEMP_SCHALTER_PRAEFIX)
      && (bild.sections ?? []).length === 0
      && (bild.boxes ?? []).some(box => box.type === 'temperature' || box.type === 'tempicon' || box.type === 'maxminicon');
  }

  /**
   * Trägt diese Karte den Schalter "Wind"? Jede Karte ohne Sidebar, die Wind-Boxen mit dem
   * gelben Umschalter hat – die NRW-Karte "Wind in Bft.". Der Schalter hat keinen eigenen
   * Zustand: er hakt alle gelben Kästchen der Wind-Boxen an bzw. ab (`ImageComponent.onWindToggle`)
   * und zeigt "an", wenn alle angehakt sind. Dieselbe Prüfung entscheidet im `ImageComponent`,
   * ob der Schalter gezeigt wird.
   */
  public static hatWindSchalter(bild: Image | null | undefined): boolean {
    return (bild?.sections ?? []).length === 0
      && (bild?.boxes ?? []).some(box => box.type === 'wind' && KiGrafikService.hatKiUmschalter(box));
  }

  /** Ist der Schalter "Temp" dieser Karte eingeschaltet – gesetzt oder per Standard? */
  public static istTemperaturAn(bild: Image | null | undefined): boolean {
    const gesetzt = (bild?.values ?? {})[KiGrafikService.TEMP_SCHALTER];
    if (gesetzt === '1' || gesetzt === '0') {
      return gesetzt === '1';
    }
    return KiGrafikService.temperaturStandard(bild);
  }

  /**
   * Standard des Schalters "Temp", solange niemand ihn umgelegt hat: **an** in den Zeilen
   * "ganz" (HEUTE, NACHT, MORGEN und die drei Aussichtentage – `…-ganz.karteN`), in allen
   * anderen Zeilen **aus**. Die Zeile steht in der `containerId` der Karte.
   */
  public static temperaturStandard(bild: Image | null | undefined): boolean {
    return /-ganz\.karte\d+$/.test(bild?.containerId ?? '');
  }

  /**
   * Dürfen die Temperaturen dieser Karte in die Anfrage? Bei Karten mit Temp-Schalter nur,
   * wenn er an ist; Karten ohne Schalter (COSMO, Lokalzeit) wie bisher immer.
   */
  private static temperaturFreigegeben(bild: Image): boolean {
    return !KiGrafikService.hatTemperaturSchalter(bild) || KiGrafikService.istTemperaturAn(bild);
  }

  /** Boxen eines Abschnitts – dieselbe Wahl wie `ImageComponent.activeBoxes`. */
  private static boxenVon(bild: Image, abschnitt: ImageSection): ImageBox[] {
    const key = KiGrafikService.abschnittsKey(abschnitt);
    let boxen: ImageBox[];
    if (abschnitt.boxes) {
      boxen = abschnitt.boxes;
    } else if (abschnitt.tempPrefix) {
      boxen = bild.boxes ?? [];
    } else {
      return [];
    }
    // Boxen können auf bestimmte Abschnitte beschränkt sein (z.B. Mondphase nur "heute Nacht").
    return boxen.filter(box => !box.sections || box.sections.includes(key));
  }

  /**
   * Trägt jede Box mit mindestens einem übernommenen Wert in ihren Zeitraum ein und merkt
   * sich in `angehakt` die Zeiträume, in denen eine Box über ihren gelben Umschalter einen
   * Wert beisteuert – ein angehaktes, aber leeres Symbol zählt nicht.
   *
   * Ebenso zählt eine Zeile als gewählt, wenn eine Karte mit eingeschaltetem
   * Temp-Schalter dort eine Temperatur beisteuert (`tempSchalterAn`): der Schalter ist eine
   * bewusste Auswahl wie der gelbe Haken, die Temperaturen sollen also auch ohne angehaktes
   * Symbol in der Anfrage stehen.
   *
   * @param temperaturErlaubt Dürfen Temperaturen dieser Karte mit (siehe
   *   {@link temperaturFreigegeben})?
   * @param tempSchalter Trägt die Karte einen Temp-Schalter? Nur dann macht eine
   *   Temperatur die Zeile zur gewählten Zeile.
   */
  private sammleBoxen(bild: Image, boxen: ImageBox[], werte: {[key: string]: string},
                      umschalter: {[key: string]: string}, zeitraum: string,
                      zeitraeume: Map<string, KiBoxEintrag[]>,
                      auswahl: KiWertauswahl, angehakt: Set<string>,
                      temperaturErlaubt: boolean, tempSchalter: boolean): void {
    const jeBox = new Map<string, KiBoxEintrag>();
    for (const box of boxen) {
      const istAngehakt = KiGrafikService.istAngehakt(box, umschalter);
      const gefunden = KiGrafikService.werteVon(box, werte, istAngehakt, auswahl, temperaturErlaubt);
      if (istAngehakt && Object.keys(gefunden).length
        > Object.keys(KiGrafikService.werteVon(box, werte, false, auswahl, temperaturErlaubt)).length) {
        angehakt.add(zeitraum);
      }
      if (tempSchalter && temperaturErlaubt && gefunden['temp'] !== undefined) {
        angehakt.add(zeitraum);
      }
      if (Object.keys(gefunden).length === 0) {
        continue;   // leer bzw. nicht übernommen: hat dem Modell nichts zu sagen
      }
      const eintraege = zeitraeume.get(zeitraum) ?? [];
      const order = istAngehakt ? KiGrafikService.orderVon(bild, box) : undefined;
      const eintrag: KiBoxEintrag = {
        name: box.region ?? box.city ?? box.id,
        karte: bild.containerId,
        werte: gefunden,
        ...(order !== undefined ? {order} : {})
      };
      eintraege.push(eintrag);
      jeBox.set(box.id, eintrag);
      zeitraeume.set(zeitraum, eintraege);
    }
    KiGrafikService.traagePfeileEin(bild, jeBox);
  }

  /**
   * Die Verbindungs-Pfeile der Karte als Felder der Quell-Einträge: `pfeil_icon` bzw.
   * `pfeil_wind` = Regionsname des Ziels. Ein Pfeil zählt nur, wenn beide Enden in der
   * Anfrage sind und beide den Wert tragen, den der Pfeil meint (Symbol bzw. Wind) – ein
   * Pfeil von einer Kombi-Box, deren Symbol nicht angehakt ist, sagt nichts. Was danach nicht
   * passt, wird still weggelassen; das Modell bekommt nur Wege, die es gehen kann.
   */
  private static traagePfeileEin(bild: Image, jeBox: Map<string, KiBoxEintrag>): void {
    const boxen = new Map((bild.boxes ?? []).map(box => [box.id, box]));
    KiGrafikService.pfeileVon(bild).forEach((nachId, vonId) => {
      const von = jeBox.get(vonId);
      const nach = jeBox.get(nachId);
      const quelle = boxen.get(vonId);
      if (!von || !nach || !quelle || !KiGrafikService.kannPfeilTragen(quelle)) {
        return;
      }
      const feld = quelle.type === 'wind' ? 'wind' : 'icon';
      if (von.werte[feld] === undefined || nach.werte[feld] === undefined) {
        return;
      }
      von.werte[feld === 'wind' ? KiGrafikService.PFEIL_WIND : KiGrafikService.PFEIL_ICON] = nach.name;
    });
  }

  /** Trägt die Box überhaupt einen gelben Umschalter? (NRW-Wind-Boxen ja, COSMO-/Lokalzeit-Wind nein) */
  private static hatKiUmschalter(box: ImageBox): boolean {
    return (box.toggles ?? []).some(t => t.id === KiGrafikService.KI_UMSCHALTER);
  }

  /** Die Reihenfolge-Nummer der Box aus `image.values` (`ki:order:<boxId>`), oder undefined. */
  private static orderVon(bild: Image, box: ImageBox): number | undefined {
    const wert = (bild.values ?? {})[KiGrafikService.ORDER_PRAEFIX + box.id];
    const nummer = Number.parseInt(wert ?? '', 10);
    return Number.isFinite(nummer) && nummer > 0 ? nummer : undefined;
  }

  /**
   * Vergibt die `order`-Nummern der Anfrage lückenlos ab 1, in der Reihenfolge der
   * Dokument-Nummern. Verschmolzene Regionen (dieselbe Region auf zwei Karten, beide
   * angehakt) zählen einmal, mit ihrer kleineren Nummer.
   */
  private static nummeriereNeu(alle: Map<string, KiBoxEintrag[]>[]): void {
    const eintraege: KiBoxEintrag[] = [];
    alle.forEach(zeitraeume => zeitraeume.forEach(liste => eintraege.push(...liste)));
    const vorhanden = [...new Set(eintraege.filter(e => e.order !== undefined).map(e => e.order!))]
      .sort((a, b) => a - b);
    const rang = new Map(vorhanden.map((nummer, i) => [nummer, i + 1]));
    eintraege.forEach(e => {
      if (e.order !== undefined) {
        e.order = rang.get(e.order);
      }
    });
  }

  /** Ist der gelbe Umschalter dieser Box gesetzt? */
  private static istAngehakt(box: ImageBox, umschalter: {[key: string]: string}): boolean {
    const key = box.id + KiGrafikService.UMSCHALTER_TRENNER + KiGrafikService.KI_UMSCHALTER;
    return umschalter[key] === '1';
  }

  /**
   * Die Werte einer Box, die in die Anfrage gehören.
   *
   * <b>Der gelbe Umschalter gilt für die Wetter-Symbole – und für Wind-Boxen, die einen
   * tragen.</b> Die NRW-Wind-Boxen haben ihn (`hatKiUmschalter`): ihr Wind geht nur
   * angehakt mit, ein nicht angehakter Wind bleibt draußen, auch bei Sturm in der
   * Schlagzeile. Wind- und Böen-Boxen ohne Umschalter (COSMO, Lokalzeit) gehen mit, sobald
   * sie **gefüllt** sind – dort sind sie Messwerte. **Temperaturen** gehen nur mit, wenn
   * `temperaturErlaubt` – auf den NRW-Karten "feine" und "sehr feine Unterteilung"
   * entscheidet das allein deren Schalter "Temp", nicht der gelbe Haken. Ein
   * Symbol dagegen ist seine Aussage über das Wetter, und die trifft er bewusst: ohne
   * gesetzten Umschalter bleibt es draußen. Dieselbe Trennung gilt für das Warnschild und
   * die freie Wert-Box – auch sie sind Aussagen, nicht Messwerte.
   *
   * Eine Kombi-Box zerfällt damit in zwei Hälften: die Temperatur einer `tempicon`-Box
   * ist immer dabei, ihr Symbol nur mit Umschalter.
   *
   * Die Schlüssel sind dieselben, unter denen das `ImageComponent` die Werte ablegt.
   */
  private static werteVon(box: ImageBox, werte: {[key: string]: string},
                          angehakt: boolean, auswahl: KiWertauswahl,
                          temperaturErlaubt = true): KiBoxWerte {
    const gefunden: KiBoxWerte = {};
    const messwerte = auswahl === 'alle';
    const temperatur = messwerte && temperaturErlaubt;
    const setze = (feld: string, wert: string | null | undefined): void => {
      if (KiGrafikService.istWert(wert)) {
        gefunden[feld] = wert as string;
      }
    };
    if (auswahl === 'kifelder') {
      // Nur die Boxen, denen das Template einen Feldnamen gibt (Lokalzeit-Prognose: Wind in
      // km/h, Regen in %, Sonne in h) – als Messwerte, also ohne Umschalter. Alles andere der
      // Karte (Temperaturen, Symbole) bleibt draußen: die Box "Wind, Regen, Sonne" soll genau
      // das und nichts weiter beschreiben.
      if (box.kiFeld) {
        setze(box.kiFeld, werte[box.id]);
      }
      return gefunden;
    }
    switch (box.type) {
      case 'temperature':
        if (temperatur) {
          setze('temp', werte[box.id]);
        }
        break;
      case 'tempicon':
        if (temperatur) {
          setze('temp', werte[box.id]);
        }
        if (angehakt) {
          setze('icon', werte[box.id + KiGrafikService.SUFFIX_ICON]);
        }
        break;
      case 'maxminicon':
        // Höchst-/Tiefstwert-Box (Samstag, AUSSICHTEN): zwei Messwerte, Symbol wie bei der Kombi-Box.
        if (temperatur) {
          setze('temp', werte[box.id]);
          setze('tmin', werte[box.id + KiGrafikService.SUFFIX_MIN]);
        }
        if (angehakt) {
          setze('icon', werte[box.id + KiGrafikService.SUFFIX_ICON]);
        }
        break;
      case 'wind':
        // In der Schlagzeile hat Wind nur als **Sturm** etwas zu sagen; darunter ist er
        // eine Zahl, die den einen Satz nur verwässert. Trägt die Box den gelben
        // Umschalter (NRW), entscheidet er zuerst: nicht angehakt heißt nicht dabei.
        if ((angehakt || !KiGrafikService.hatKiUmschalter(box))
          && (messwerte || KiGrafikService.istSturm(box, werte))) {
          setze('wind', werte[box.id]);
          setze('boeen', werte[box.id + KiGrafikService.SUFFIX_BOEEN]);
          setze('dir', werte[box.id + KiGrafikService.SUFFIX_DIR]);
        }
        break;
      case 'boeen':
        // Böen-Box (Lokalzeit Aachen, Aussichtentage): ein Messwert wie der Wind der Wind-Box.
        if (messwerte || KiGrafikService.istSturm(box, werte)) {
          setze('boeen', werte[box.id]);
        }
        break;
      case 'icon':
        if (angehakt) {
          setze('icon', werte[box.id]);
        }
        break;
      case 'danger':
        if (angehakt) {
          setze('warnschild', werte[box.id]);
        }
        break;
      default:
        if (angehakt) {
          setze('wert', werte[box.id]);
        }
    }
    return gefunden;
  }

  /**
   * Meldet diese Wind-Box einen **Sturm**?
   *
   * Maßgeblich ist die Beaufort-Skala: Sturm beginnt bei {@link STURM_AB} – "Sturm" bzw.
   * "Sturmböen". Es genügt **eines** von beidem: ein Mittelwind von 9 ist Sturm, und
   * Sturmböen sind es auch, wenn der Mittelwind darunter liegt (was der Normalfall ist).
   *
   * Nicht gefüllte oder unlesbare Felder gelten als kein Sturm – im Zweifel bleibt der
   * Wert aus der Schlagzeile heraus, statt eine Warnung zu erfinden.
   */
  private static istSturm(box: ImageBox, werte: {[key: string]: string}): boolean {
    const mittel = KiGrafikService.alsBft(werte[box.id]);
    const boeen = KiGrafikService.alsBft(werte[box.id + KiGrafikService.SUFFIX_BOEEN]);
    if (mittel < KiGrafikService.STURM_AB && boeen < KiGrafikService.STURM_AB) {
      return false;
    }
    // Ein Sturm in der Schlagzeile ist die Ausnahme von der Regel – im Protokoll soll
    // nachvollziehbar sein, welcher Wert sie ausgelöst hat.
    console.debug(`KI-Grafik: Sturm in "${box.region ?? box.id}" kommt in die Schlagzeile – `
      + `Mittelwind ${mittel} (${KiGrafikService.windBezeichnung(mittel)}), `
      + `Böen ${boeen} (${KiGrafikService.windBezeichnung(boeen, true)})`);
    return true;
  }

  /** Eine Bft-Angabe als Zahl; alles Unlesbare zählt als 0. */
  private static alsBft(wert: string | number | null | undefined): number {
    const zahl = Number.parseInt(String(wert ?? ''), 10);
    return Number.isFinite(zahl) ? zahl : 0;
  }

  /**
   * Benennung einer Bft-Zahl nach den Skalen oben – für Meldungen im Protokoll.
   *
   * Der Client schickt in der Anfrage weiter die **Zahl**, nicht den Text: das Modell
   * kennt die Skala. Beim Nachvollziehen einer Anfrage im Log hilft der Klartext aber
   * sehr ("boeen 10 = schwere Sturmböen").
   */
  public static windBezeichnung(bft: number, boeen = false): string {
    if (boeen) {
      return KiGrafikService.BOEEN.get(bft) ?? `Bft ${bft}`;
    }
    return KiGrafikService.MITTELWIND[bft] ?? `Bft ${bft}`;
  }

  /**
   * Ist das ein echter Wert?
   *
   * ⚠️ `null` muss mitgeprüft werden, nicht nur `undefined`: der Rest-Service
   * serialisiert nicht gesetzte Felder als **`null`**, und `null !== undefined` ist in
   * JavaScript wahr. Dieselbe Prüfung wie `ImageComponent.istWert`.
   */
  private static istWert(wert: string | null | undefined): boolean {
    return wert !== undefined && wert !== null && wert !== '';
  }

  /**
   * Zeitraum-Kennung einer Karte: das Zeilen-Segment ihrer `containerId`.
   *
   * `wdr.grafik.heute-ganz.karte1` -> `heute_ganz`, ebenso
   * `wdr.lokalzeit.aachen.heute-ganz.karte1` und `wdr.cosmo.heute-ganz.karte1`.
   * Adressiert wird über die `containerId` und nicht über die Zeilenüberschrift, weil
   * die sich zwischen den Ober-Reitern wiederholt ("HEUTE: ganz" gibt es dreimal).
   */
  private static zeitAus(containerId: string | undefined): string {
    const segmente = (containerId ?? '').split('.').filter(teil => teil.length > 0);
    if (segmente.length === 0) {
      return 'unbekannt';
    }
    const letztes = segmente[segmente.length - 1];
    const zeile = segmente.length > 1 && /^karte\d+$/.test(letztes)
      ? segmente[segmente.length - 2]
      : letztes;
    return KiGrafikService.slug(zeile);
  }

  /** Abschnitts-Schlüssel wie im `ImageComponent`: die Id, ersatzweise der Titel. */
  private static abschnittsKey(abschnitt: ImageSection): string {
    return abschnitt.id ?? abschnitt.title;
  }

  /** Gebietsname aus der Reiter-Beschriftung: "Nordrhein-Westfalen" -> "Nordrhein Westfalen". */
  private static gebietsname(caption: string): string {
    return (caption ?? '').replace(/-/g, ' ').trim();
  }

  /** Kennung für das JSON: klein geschrieben, Bindestriche zu Unterstrichen. */
  private static slug(text: string): string {
    return (text ?? '').trim().toLowerCase().replace(/-/g, '_');
  }

  /** Die gesammelten Zeiträume in der Form, die der Service erwartet. */
  private static alsZeiten(zeitraeume: Map<string, KiBoxEintrag[]>): KiZeit[] {
    const zeiten: KiZeit[] = [];
    zeitraeume.forEach((eintraege, zeitraum) => {
      zeiten.push({[zeitraum]: [KiGrafikService.alsRegionen(eintraege)]});
    });
    return zeiten;
  }

  /**
   * Die Boxen eines Zeitraums als Regionen-Objekt.
   *
   * <b>Gleicher Name, verschiedene Karten – ein Eintrag.</b> Verschmolzen wird
   * ausschließlich bei **identischem** Regionsnamen, und nur solange sich die **Felder**
   * nicht überschneiden: `{"Niederrhein": {"temp": "17", "wind": "3", "boeen": "5",
   * "dir": "9"}}`. Das Modell bekommt so alles über ein Gebiet an einer Stelle, statt es
   * aus zwei künstlich getrennten Einträgen zusammensuchen zu müssen.
   *
   * Die Karte "feine Unterteilung" (8 Gebiete) und die Wind-Karte (9 Gebiete) haben
   * **vier** Namen gemeinsam, und die Stationstabellen des Rest-Service belegen, dass es
   * dabei um dasselbe Gebiet geht:
   *
   * | Name | Symbol/Temperatur | Wind |
   * | --- | --- | --- |
   * | Ruhrgebiet | AW0034 Bochum | AW0034 Bochum |
   * | Münsterland | K00013 Münster | K00013 Münster |
   * | Eifel | H212XX Dahlem | H212XX Dahlem |
   * | Niederrhein | AW0150 Issum | H203XX **Kleve** |
   *
   * ⚠️ **"Niederrhein" ist der eine Grenzfall:** dort messen die beiden Karten an
   * verschiedenen Stationen, und die Wind-Karte führt zusätzlich ein eigenes "Mittlerer
   * Niederrhein" (Düsseldorf) – ihr "Niederrhein" meint also den nördlichen Teil, der
   * der Temperatur-Karte das ganze Gebiet. Beide Werte beschreiben trotzdem den
   * Niederrhein, deshalb bleibt es beim Verschmelzen; wer das trennen will, muss hier
   * ansetzen.
   *
   * ⚠️ **Ähnliche Namen sind keine gleichen.** "Sauerland" (Temperatur) und
   * "Hochsauerland" (Wind) bleiben zwei Einträge, ebenso "Kölner Bucht" und
   * "Köln-Bonner-Bucht" – der Vergleich ist eine Zeichenkettengleichheit und darf keine
   * Ähnlichkeitssuche werden. Die Wind-Karte führt fünf Gebiete, die es auf der
   * Temperatur-Karte gar nicht gibt (Hochsauerland, Köln-Bonner-Bucht, Mindener Land und
   * Lipperland, Mittlerer Niederrhein, Weserbergland).
   *
   * Überschneiden sich die Felder – etwa bei den sieben namenlosen Wert-Boxen einer
   * Zeile, die alle `wert` heißen und alle ein `warnschild` tragen –, dann bekommt
   * **jeder** Eintrag der Gruppe die `containerId` seiner Karte vorangestellt. Alle,
   * nicht nur die späteren: sonst hinge das Ergebnis an der Sammelreihenfolge.
   *
   * Über Zeiträume und Gebiete hinweg wird nie verschmolzen – jeder Zeitraum hat seine
   * eigene Liste. Das ist wichtig, weil "Eifel" in der NRW-Tabelle und in der
   * Lokalzeit-Tabelle des Rest-Service Verschiedenes meint: NRW- und Lokalzeit-Karten
   * liegen in verschiedenen Gebieten, ihre Einträge können sich also nicht treffen.
   */
  private static alsRegionen(eintraege: KiBoxEintrag[]): KiRegionen {
    const gruppen = new Map<string, KiBoxEintrag[]>();
    for (const eintrag of eintraege) {
      gruppen.set(eintrag.name, [...(gruppen.get(eintrag.name) ?? []), eintrag]);
    }
    // Nummerierte Regionen zuerst, in ihrer Reihenfolge; der Rest dahinter wie eingesammelt.
    // Das JSON spiegelt damit die Reihenfolge, die das Feld "order" ohnehin vorgibt.
    const mitOrder = (gruppe: KiBoxEintrag[]): number | undefined =>
      gruppe.reduce<number | undefined>((min, e) =>
        e.order === undefined ? min : (min === undefined ? e.order : Math.min(min, e.order)), undefined);
    const sortiert = [...gruppen.entries()].sort(([, a], [, b]) => {
      const oa = mitOrder(a);
      const ob = mitOrder(b);
      if (oa === undefined && ob === undefined) {
        return 0;
      }
      return oa === undefined ? 1 : ob === undefined ? -1 : oa - ob;
    });
    const regionen: KiRegionen = {};
    const mitNummer = (werte: KiBoxWerte, order: number | undefined): KiBoxWerte =>
      order === undefined ? werte : {order, ...werte};
    sortiert.forEach(([name, gruppe]) => {
      if (KiGrafikService.verschmelzbar(gruppe)) {
        regionen[name] = mitNummer(Object.assign({}, ...gruppe.map(e => e.werte)) as KiBoxWerte, mitOrder(gruppe));
        return;
      }
      for (const eintrag of gruppe) {
        regionen[`${eintrag.karte}.${name}`] = mitNummer(eintrag.werte, eintrag.order);
      }
    });
    return regionen;
  }

  /** Tragen die Einträge einer Gruppe paarweise verschiedene Felder? */
  private static verschmelzbar(gruppe: KiBoxEintrag[]): boolean {
    const gesehen = new Set<string>();
    for (const eintrag of gruppe) {
      for (const feld of Object.keys(eintrag.werte)) {
        if (gesehen.has(feld)) {
          return false;
        }
        gesehen.add(feld);
      }
    }
    return true;
  }

  /**
   * Der Wind von COSMO, **programmatisch** entschieden statt vom Modell.
   *
   * Das Modell hielt sich nicht verlässlich an die Regel "Wind nur bei Böen ab 6, je
   * Region der höchste Wert". Deshalb geht der Wind gar nicht erst roh ins JSON: je
   * Zeitraum und COSMO-Region (NRW, Bremen, Berlin) bleibt **höchstens ein** Wind-Eintrag –
   * der der Box mit der höchsten Windstärke (der höhere von Böen und Mittelwind) –, und auch
   * der nur, wenn diese mindestens {@link COSMO_WIND_AB} erreicht. Für NRW heißt er "NRW"
   * statt nach der Teilregion (Münsterland, Eifel …), Bremen und Berlin behalten ihren
   * Namen und verschmelzen so mit ihrer Temperatur-Box.
   *
   * Nur Wind-Einträge von COSMO-Karten werden angefasst; NRW und Lokalzeit bleiben, wie
   * sie sind. Ein Zeitraum, der danach leer ist, fällt weg.
   */
  private static verdichteCosmoWind(zeitraeume: Map<string, KiBoxEintrag[]>): void {
    for (const [zeitraum, eintraege] of [...zeitraeume.entries()]) {
      const staerkster = new Map<string, {eintrag: KiBoxEintrag; staerke: number}>();
      const rest: KiBoxEintrag[] = [];
      for (const eintrag of eintraege) {
        if (!eintrag.karte?.startsWith(KiGrafikService.COSMO_PRAEFIX)
          || !KiGrafikService.istWindEintrag(eintrag)) {
          rest.push(eintrag);
          continue;
        }
        const region = KiGrafikService.COSMO_REGIONEN_AUSSER_NRW.includes(eintrag.name)
          ? eintrag.name : KiGrafikService.COSMO_NRW;
        const staerke = Math.max(KiGrafikService.alsBft(eintrag.werte['wind']),
          KiGrafikService.alsBft(eintrag.werte['boeen']));
        const bisher = staerkster.get(region);
        if (!bisher || staerke > bisher.staerke) {
          staerkster.set(region, {eintrag, staerke});
        }
      }
      staerkster.forEach(({eintrag, staerke}, region) => {
        if (staerke >= KiGrafikService.COSMO_WIND_AB) {
          rest.push({...eintrag, name: region});
        } else {
          console.debug(`KI-Grafik: COSMO-Wind ${region} in ${zeitraum} bleibt weg `
            + `(höchstens Bft ${staerke})`);
        }
      });
      if (rest.length > 0) {
        zeitraeume.set(zeitraum, rest);
      } else {
        zeitraeume.delete(zeitraum);
      }
    }
  }

  /** Trägt der Eintrag Windwerte (Wind-Box)? */
  private static istWindEintrag(eintrag: KiBoxEintrag): boolean {
    return ['wind', 'boeen', 'dir'].some(feld => feld in eintrag.werte);
  }

  /** Anzahl der eingesammelten Boxen – für die Rückmeldung an den Redakteur. */
  private static zaehle(zeitraeume: Map<string, KiBoxEintrag[]>): number {
    let anzahl = 0;
    zeitraeume.forEach(eintraege => anzahl += eintraege.length);
    return anzahl;
  }

  /**
   * Holt KI-Antworten zurück, die ein **älterer** Client im Dokumentbaum abgelegt hat
   * (unter `ki:reiter` / `ki:zeile` in `image.values` der ersten Karte ihres Umfangs).
   *
   * Ein neu erzeugtes Ergebnis landet dort nicht mehr: die grünen Boxen zeigten zwar
   * dieselbe Antwort nach einem Neuladen wieder an, das Ablegen machte aber jedes
   * Speichern messbar langsamer – bei Dutzenden Reiter-/Zeilen-Umfängen mit jeweils
   * eigenem JSON-Blob je Karte kommt das im ohnehin schon mehrere MB großen WDR-Dokument
   * spürbar zusammen. Die Box lebt seither ausschließlich im `KiErgebnisService`-Signal,
   * also für die laufende Sitzung – **dieses** Lesen bleibt aber bestehen, damit vor der
   * Umstellung gespeicherte Datensätze ihre Boxen beim Öffnen nicht verlieren.
   *
   * Gelesen wird jede Karte: die reservierten Schlüssel tragen ihren Umfang selbst, es
   * braucht also keine Annahme über die Baumstruktur. Unlesbare Einträge werden
   * übersprungen und nicht gemeldet – ein Datensatz aus einer älteren Fassung darf das
   * Öffnen nicht stören.
   */
  public ladeErgebnisse(document: DocumentModel): GespeichertesKiErgebnis[] {
    const gefunden: GespeichertesKiErgebnis[] = [];
    this.durchlaufeKarten(document.tabs ?? [], karte => {
      for (const art of KiGrafikService.ARTEN) {
        const wert = (karte.values ?? {})[KiGrafikService.ABLAGE_PRAEFIX + art];
        const gelesen = wert ? KiGrafikService.leseAblage(wert, art) : null;
        if (gelesen) {
          gefunden.push(gelesen);
        }
      }
    });
    return gefunden;
  }

  /** Läuft alle Karten des Dokuments ab. */
  private durchlaufeKarten(container: Container[], besuch: (karte: Image) => void): void {
    for (const kind of container) {
      if (kind.containerType === 'Image') {
        besuch(kind as Image);
      }
      this.durchlaufeKarten(kind.containerList ?? [], besuch);
    }
  }

  /** Ein abgelegter Eintrag zurück als Umfang plus Antwort, oder null wenn unlesbar. */
  private static leseAblage(wert: string, art: GrafikUmfangArt): GespeichertesKiErgebnis | null {
    try {
      const rohe = JSON.parse(wert) as {
        name?: string; art?: GrafikUmfangArt; kartenIds?: string[] | null;
        texte?: KiTextEintrag[];
        // Fassung mit genau einem Text – so lagen die ersten gespeicherten Datensätze da.
        text?: string; boxen?: number; uebernommen?: boolean; ziel?: string; zeitpunkt?: string;
      };
      if (!rohe.kartenIds || rohe.kartenIds.length === 0) {
        return null;
      }
      const rohtexte = rohe.texte ?? (rohe.text
        ? [{ziel: rohe.ziel ?? '', text: rohe.text, boxen: rohe.boxen ?? 0,
          uebernommen: rohe.uebernommen !== false, zeitpunkt: rohe.zeitpunkt} as never]
        : []);
      const texte = (rohtexte as unknown as {
        ziel?: string; text?: string; boxen?: number; uebernommen?: boolean; zeitpunkt?: string;
      }[])
        .filter(eintrag => !!eintrag?.text)
        .map(eintrag => ({
          ziel: eintrag.ziel ?? '',
          text: eintrag.text as string,
          boxen: eintrag.boxen ?? 0,
          uebernommen: eintrag.uebernommen !== false,
          zeitpunkt: KiGrafikService.alsDatum(eintrag.zeitpunkt)
        }));
      if (texte.length === 0) {
        return null;
      }
      return {
        umfang: new GrafikUmfang(rohe.kartenIds, rohe.art ?? art, rohe.name ?? ''),
        ergebnis: {name: rohe.name ?? '', texte}
      };
    } catch {
      return null;   // Datensatz aus einer älteren Fassung – Öffnen darf nicht scheitern
    }
  }

  /** Ein abgelegter Zeitstempel als Datum; alles Unlesbare wird zu "jetzt". */
  private static alsDatum(wert: string | undefined): Date {
    const datum = wert ? new Date(wert) : new Date();
    return isNaN(datum.getTime()) ? new Date() : datum;
  }

  private findeContainer(container: Container[], containerId: string): Container | undefined {
    for (const kind of container) {
      if (kind.containerId === containerId) {
        return kind;
      }
      const tiefer = this.findeContainer(kind.containerList ?? [], containerId);
      if (tiefer) {
        return tiefer;
      }
    }
    return undefined;
  }
}
